import type { AgencyId } from "@inrev/common";
import {
	type FieldPath,
	type FieldValues,
	type UseControllerProps,
	useController,
} from "react-hook-form";
import { FormFieldError } from "../FormFieldError";
import { AgencySelectorInput } from "../inputs/AgencySelectorInput";

export type FormAgencySelectorProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, FieldPath<TFieldValues>, TTransformedValues> & {
	excludeAgencyId?: AgencyId;
	disabled?: boolean;
	readonly?: boolean;
	compact?: boolean;
	optionsClassName?: string;
	placeholder?: string;
	errorMessage?: string;
	testId?: string;
};

export const FormAgencySelector = <
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
>({
	name,
	control,
	defaultValue,
	excludeAgencyId,
	disabled,
	readonly,
	compact,
	optionsClassName,
	placeholder,
	errorMessage,
	testId,
}: FormAgencySelectorProps<TFieldValues, TTransformedValues>) => {
	const {
		field: { ref: _ref, onBlur, onChange, value },
		fieldState: { error },
	} = useController<TFieldValues, FieldPath<TFieldValues>, TTransformedValues>({
		name,
		control,
		defaultValue,
	});

	const hasError = !!error;

	const handleChange = (next: AgencyId | null) => {
		onChange(next);
		onBlur();
	};

	return (
		<div className="relative">
			<AgencySelectorInput
				value={value ?? null}
				onChange={handleChange}
				excludeAgencyId={excludeAgencyId}
				disabled={disabled}
				readonly={readonly}
				compact={compact}
				error={hasError}
				placeholder={placeholder}
				optionsClassName={optionsClassName}
				testId={testId}
			/>
			{hasError && <FormFieldError error={error} errorMessage={errorMessage} />}
		</div>
	);
};
